import * as messagingAPI from '../../lib/api/messaging'
import * as fileAPI from '../../lib/api/file'
import * as loadingActions from './loading'
import {pushRoute, popRoute} from './router'
import {isLoading} from '../../lib/util/loading'
import WS from '../../lib/websocket'
import ValidationError from '../../lib/error/validation-error'
import {mimes as allowedAttachmentMimes, maxFilesizeMB as maxAttachmentSizeMB} from '../../lib/data/allowed-attachments'
import moment from 'moment'
import objectPath from 'object-path'



export const THREAD_STATUS_INBOX 					= 'inbox'
export const THREAD_STATUS_ARCHIVED					= 'archived'

export const THREADS_IS_LOADING_INBOX				= 'THREADS_IS_LOADING_REQUEST_INBOX'
export const THREADS_IS_LOADING_ARCHIVED			= 'THREADS_IS_LOADING_REQUEST_ARCHIVED'

export const THREADS_LOAD_SUCCESS  					= 'THREADS_LOAD_SUCCESS'
export const UPDATE_UNREAD_COUNT					= 'THREADS_UPDATE_COUNT'

export const THREAD_SET_CURRENT 					= 'THREAD_SET_CURRENT'
export const THREAD_SET_CURRENT_MESSAGES 			= 'THREAD_SET_CURRENT_MESSAGES'

export const THREAD_ARCHIVE 						= 'THREAD_ARCHIVE'
export const THREAD_ARCHIVE_UNDO					= 'THREAD_ARCHIVE_UNDO'
export const THREAD_CLEAR_NOTIFICATION				= 'THREAD_CLEAR_NOTIFICATION'
export const THREAD_CLOSE							= 'THREAD_CLOSE'

export const THREAD_UPDATE_DRAFT					= 'THREAD_UPDATE_DRAFT'
export const THREAD_DRAFT_TO_MESSAGE				= 'THREAD_DRAFT_TO_MESSAGE'
export const THREAD_CLEAR_DRAFT						= 'THREAD_CLEAR_DRAFT'
export const THREAD_UPDATE_DRAFT_ADD_ATTACHMENT		= 'THREAD_UPDATE_DRAFT_ADD_ATTACHMENT'
export const THREAD_UPDATE_DRAFT_UPDATE_ATTACHMENT 	= 'THREAD_UPDATE_DRAFT_UPDATE_ATTACHMENT'
export const THREAD_UPDATE_DRAFT_REMOVE_ATTACHMENT 	= 'THREAD_UPDATE_DRAFT_REMOVE_ATTACHMENT'

export const THREAD_REPLY_MESSAGE					= 'THREAD_REPLY_MESSAGE'

export const THREAD_COMPOSE							= 'THREAD_COMPOSE'
export const THREAD_ADD_FROM_DRAFT					= 'THREAD_ADD_FROM_DRAFT'

export const THREAD_READ							= 'THREAD_READ'
export const THREAD_SET_UPDATED						= 'THREAD_UPDATED'
export const THREAD_STATUS_TYPE_CHANGED				= 'THREAD_CURRENT_STATUS_TYPE'

export const THREAD_LOAD_CATEGORIES					= 'THREAD_LOAD_CATEGORIES'

export const defaultAttachmentOptions = {
	cameraType: 'back', // 'front' or 'back'
	mediaType: 'photo', // 'photo' or 'video'
	allowsEditing: false,
	allowsDocumentSelection: true,
	noData: true,
	storageOptions: {
		skipBackup: true, // ios only - image will NOT be backed up to icloud
		path: 'images' // ios only - will save image at /Documents/images rather than the root
	},
	mimeFilter: '*/*'
}

/**
 * Loads threads
 * @param count
 * @param offset
 * @param threadStatus
 * @returns {function()}
 */
export function loadThreads(count, offset, threadStatus, keepHiddenThreads){
	return (dispatch, getState) => {
		const {Loading} = getState()

		// Update unread count
		if(offset === 0){
			dispatch(updateUnreadCount())
		}

		let isLoadingConst
		switch(threadStatus){
			case THREAD_STATUS_INBOX:
				isLoadingConst = THREADS_IS_LOADING_INBOX
				break
			case THREAD_STATUS_ARCHIVED:
				isLoadingConst = THREADS_IS_LOADING_ARCHIVED
				break

			default:
				throw new Error(`Unknown thread status: ${threadStatus}`)
		}
		if(isLoading(Loading[isLoadingConst])) return Promise.resolve()

		dispatch(loadingActions.isLoading(isLoadingConst))

		return messagingAPI.loadThreads(count, offset, threadStatus)
			.then(res => {
				dispatch(_receiveThreads(threadStatus, offset, res, keepHiddenThreads))
				dispatch(loadingActions.finishedLoading(isLoadingConst))
			}).catch(err => {
				dispatch(loadingActions.finishedLoading(isLoadingConst, err))
			})
	}
}

function _receiveThreads(threadStatus, pageOffset, threads, keepHiddenThreads){
	return {
		type: THREADS_LOAD_SUCCESS,
		pageOffset: pageOffset,
		threadStatus: threadStatus,
		threads: threads,
		keepHiddenThreads: keepHiddenThreads
	}
}

export function updateUnreadCount(){
	return (dispatch) => dispatch(
		loadingActions.thunk(
			UPDATE_UNREAD_COUNT,
			messagingAPI.loadUnreadCount().then(
				(count) => dispatch(_receiveUnreadCount(count) )
			)
		)
	)
}

function _receiveUnreadCount(count){
	return {
		type: UPDATE_UNREAD_COUNT,
		count: count
	}
}

/**
 * Sets the current route to the thread and triggers loading of the thread
 * @param thread
 * @returns {function()}
 */
export function openThread(thread_or_id, navigation=pushRoute){
	return (dispatch) => {
		dispatch(navigation('ViewThread'))
		const threadId = typeof thread_or_id === 'number' ? thread_or_id : thread_or_id.Id
		return Promise.all([
			dispatch(_setOrLoadCurrentThread(thread_or_id)),
			dispatch(loadMessages(threadId))
		])
			.then(() => dispatch(markThreadAsRead(threadId)))
	}
}

export function closeThread(){
	return {
		type: THREAD_CLOSE
	}
}

/**
 * Loads the current thread from remote or from existing data (passed in via param)
 * @param thread_or_id
 * @returns {function()}
 * @private
 */
function _setOrLoadCurrentThread(thread_or_id){
	return (dispatch) => {
		dispatch(loadingActions.isLoading(THREAD_SET_CURRENT))

		return (typeof thread_or_id === 'number' ? messagingAPI.loadThreadInfo(thread_or_id) : Promise.resolve(thread_or_id)).then((thread) => {
			dispatch(_receiveCurrentThread(thread))
			dispatch(loadingActions.finishedLoading(THREAD_SET_CURRENT))
		})
	}
}

/**
 * Loads messages for the current thread
 * @returns {function()}
 */
export function loadMessages(threadId){
	return (dispatch) => loadingActions.promise(
		dispatch,
		THREAD_SET_CURRENT_MESSAGES,
		messagingAPI.loadMessages(threadId).then(
			(messages) => dispatch(_receiveCurrentMessages(messages))
		)
	)
}

/**
 * Action for receiving data for the thread
 * @param thread
 * @returns {{type: string, thread: *}}
 * @private
 */
function _receiveCurrentThread(thread){
	return {
		type: THREAD_SET_CURRENT,
		thread: thread
	}
}

/**
 * Action for receiving data for messages
 * @param messages
 * @returns {{type: string, messages: *}}
 * @private
 */
function _receiveCurrentMessages(messages){
	return {
		type: THREAD_SET_CURRENT_MESSAGES,
		messages: messages
	}
}

/**
 * Archive the current thread
 * @param threadId set to null for the current thread
 */
export function archiveThread(threadId=false, isArchived=true){
	return (dispatch, getState) => {
		if(threadId === false){
			const currentThread = getState().Messaging.current.thread
			if(currentThread === false){
				throw new Error('Current thread has not been set')
			}else{
				threadId = currentThread.Id
				dispatch(popRoute()) // open thread view -> inbox
			}
		}

		dispatch(_setThreadArchivedState(threadId, isArchived))

		return loadingActions.promise(
			dispatch,
			THREAD_ARCHIVE,
			messagingAPI.archiveThread(threadId, isArchived))
			.then((id) => WS.waitFor(id))
			.then(() => dispatch(loadThreads(20, 0, THREAD_STATUS_ARCHIVED)))
	}
}

/**
 * Sets the thread archived state
 * @param threadId
 * @param isArchived
 * @returns {{type: string, threadId: *}}
 * @private
 */
function _setThreadArchivedState(threadId, isArchived=true){
	return {
		type: isArchived ? THREAD_ARCHIVE : THREAD_ARCHIVE_UNDO,
		threadId: threadId
	}
}

export function clearNotification(){
	return {
		type: THREAD_CLEAR_NOTIFICATION
	}
}

/**
 * Update/set the draft content
 * @param threadId
 * @param content
 * @param attachments
 * @returns {{type: object}}
 */
export function updateDraft(threadId=undefined, content=undefined, extra={}){
	if(arguments.length == 0){
		throw new Error('Must pass atleast one parameter to updateDraft')
	}

	return {
		type: THREAD_UPDATE_DRAFT,
		threadId: threadId || 'default',
		...(content !== undefined ? {content: content} : {}),
		...extra
	}
}



/**
 * Adds a single attachment to the draft
 * @param threadId
 * @param attachment
 */
export function addAttachmentToDraft(threadId='default', attachment){
	return (dispatch) => {

		// IOS doesn't provide the mimetype
		if(!attachment.type){
			attachment.type = require('react-native-mime-types').lookup(attachment.fileName)
		}

		attachment.FileName = attachment.fileName

		// 10MB file limit
		if(attachment.fileSize > (maxAttachmentSizeMB * Math.pow(1024, 2))){
			attachment.progress = 'sizeerror'
			dispatch(_addAttachmentToDraft(threadId, attachment))

			return Promise.reject(new Error("File too large"))
		}else if(!allowedAttachmentMimes.includes(attachment.type)){
			attachment.progress = 'typeerror'

			dispatch(_addAttachmentToDraft(threadId, attachment))
			return Promise.reject(new Error("Not a allowed file type"))
		}else{
			if(!(attachment.progress !== undefined || attachment.cancelUpload)){

				dispatch(_addAttachmentToDraft(threadId, attachment))
				return dispatch(retryUploadAttachment(threadId, attachment))
			}
		}
	}
}

function _addAttachmentToDraft(threadId, attachment){
	return {
		type: THREAD_UPDATE_DRAFT_ADD_ATTACHMENT,
		threadId: threadId,
		attachment: attachment
	}
}

/**
 * Retry to upload an attachment
 * @param threadId
 * @param attachment
 * @returns {function()}
 */
export function retryUploadAttachment(threadId=undefined, attachment){
	return (dispatch) => {
		if(attachment.asMutable){
			attachment = attachment.asMutable()
		}

		let {promise, cancel} = fileAPI.upload(attachment, (e) => {
			if(e.lengthComputable){
				attachment.progress = e.loaded / e.total

				setTimeout(() => dispatch(updateAttachmentFromDraft(threadId, attachment)), 50)
			}
		})

		attachment.progress = 'uploading'
		attachment.cancelUpload = cancel

		dispatch(updateAttachmentFromDraft(threadId, attachment))

		return promise
			.then((data) => {
				delete attachment.cancelUpload
				attachment.progress = 'processing'
				dispatch(updateAttachmentFromDraft(threadId, attachment))

				return WS.waitFor(data.ClientNotificationId, 120000)
			})
			.then((event) => {
				attachment.id = event.ItemId
				attachment.progress = 'uploaded'
				dispatch(updateAttachmentFromDraft(threadId, attachment))
			})
			.catch((e) => {
				console.log("Upload ERROR", e)
				attachment.progress = 'error'
				dispatch(updateAttachmentFromDraft(threadId, attachment))
			})
	}
}

/**
 * Updates an attachment within a draft
 * @param threadId
 * @param attachment
 * @returns {{type: string, threadId: undefined, attachment: *}}
 */
export function updateAttachmentFromDraft(threadId='default', attachment){
	return {
		type: THREAD_UPDATE_DRAFT_UPDATE_ATTACHMENT,
		threadId: threadId,
		attachment: attachment
	}
}

/**
 * Removes an attachment from a draft
 * @param threadId
 * @param fileName
 */
export function removeAttachmentFromDraft(threadId='default', fileName){
	return (dispatch, getState) => {
		const attachments = getState().Messaging.drafts[threadId].attachments

		if(attachments){
			const attachment = attachments.find((attachment) => attachment.fileName == fileName)
			if(attachment.cancelUpload){
				attachment.cancelUpload()
			}
		}

		dispatch(_removeAttachmentFromDraft(threadId, fileName))
	}
}

function _removeAttachmentFromDraft(threadId, fileName){
	return {
		type: THREAD_UPDATE_DRAFT_REMOVE_ATTACHMENT,
		threadId: threadId,
		fileName: fileName
	}
}

export function updateDraftSubject(subject){
	return updateDraft(undefined, undefined, {
		subject: subject
	})
}

export function updateDraftCategory(category){
	return updateDraft(undefined, undefined, {
		category: category
	})
}

/**
 * Clear the draft
 * @param threadId
 * @returns {{type: string, threadId: *}}
 */
export function clearDraft(threadId=null){
	return {
		type: THREAD_CLEAR_DRAFT,
		threadId: threadId
	}
}

/**
 * Turns the current draft into a message
 * @param threadId
 * @returns {{type: string, threadId: *}}
 */
export function draftToMessage(threadId){
	return {
		type: THREAD_DRAFT_TO_MESSAGE,
		threadId: threadId
	}
}

/**
 * Reply to a thread
 * @param threadId
 * @param message
 * @param attachments
 * @returns {function(): (Promise.<T>|Promise.<T>)}
 */
export function replyToThread(threadId, message, attachments){
	return (dispatch, getState) =>
		loadingActions.promise(
			dispatch,
			[THREAD_REPLY_MESSAGE, threadId],
			messagingAPI.reply(threadId, message, attachments)
				.then((id) => WS.waitFor(id))
				.then(() => dispatch(draftToMessage(threadId)))
				.then(() => dispatch(_setThreadUpdated(threadId, moment().format(), getState().Auth.username)))
		)
}

/**
 * Change the read status of the current thread
 * @param threadId for the thread
 */
export function markThreadAsRead(threadId){
	return (dispatch, getState) => {
		const retries = 3
		const thread = getState().Messaging[THREAD_STATUS_INBOX].threads.find((thread) => thread.Id === threadId)

		if(thread && thread.UnreadMessageCount === 0){
			return Promise.resolve()
		}else{
			dispatch(_setThreadReadState(threadId))

			const retryMarkThreadAsRead = (threadId, retries) => {
				return messagingAPI.markThreadAsRead(threadId)
					.then((id) => WS.waitFor(id))
					.catch((e) => {
						if(retries == 0){
							return Promise.reject(e)
						}else{
							return retryMarkThreadAsRead(threadId, retries - 1)
						}
					})
			}

			return retryMarkThreadAsRead(threadId, retries)
		}
	}
}

/**
 * Sets the thread read state
 * @param threadId
 * @returns {{type: string, threadId: *}}
 * @private
 */
function _setThreadReadState(threadId){
	return {
		type: THREAD_READ,
		threadId: threadId
	}
}

/**
 * Sets the thread updated on date
 * @param threadId
 * @param updatedOn
 * @returns {{type: string, threadId: *}}
 * @private
 */
function _setThreadUpdated(threadId, updatedOn, updatedBy){
	return {
		type: THREAD_SET_UPDATED,
		threadId: threadId,
		updatedOn: updatedOn,
		updatedBy: updatedBy
	}
}

/**
 * keeps track of the currently selected thread list
 * @param threadStatusType
 * @returns {{type: string, currentStatus: *}}
 */
export function changeActiveThreadType(threadStatusType){
	return {
		type: THREAD_STATUS_TYPE_CHANGED,
		currentStatus: threadStatusType
	}
}


/**
 * removes the current thread draft and adds it into the thread list
 * @param newThreadId
 */
export function addThreadAndRemoveDraft(thread){
	return {
		type: THREAD_ADD_FROM_DRAFT,
		thread: thread
	}
}

/**
 * Create a thread from the current draft
 * @returns {function(): (Promise.<T>|Promise.<T>)}
 */
export function composeThreadFromCurrentDraft(){
	return (dispatch, getState) => {
		const state = getState()

		const {subject, category, content, attachments} = objectPath.get(state, ['Messaging', 'drafts', 'default'], {})

		if(!category || category === ''){
			return Promise.reject(new ValidationError('category', 'Please select a category'))
		}else if(!subject || subject.length === 0 || subject.match(/^ +$/)){
			return Promise.reject(new ValidationError('subject', 'Please enter a subject'))
		}else if(!content || content.length === 0 || content.match(/^ +$/)){
			return Promise.reject(new ValidationError('content', 'Please enter a message'))
		}else if(attachments && attachments.filter((attachment) => attachment.progress !== 'uploaded').length > 0){
			return Promise.reject(new ValidationError('Attachments', 'Please wait until the attachments have uploaded or remove any invalid attachments'))
		}

		return loadingActions.promise(
			dispatch,
			THREAD_COMPOSE,
			messagingAPI.compose(subject, category, content, attachments)
				.then((id) => WS.waitFor(id))
				.then((result) => {
					const cat = state.Messaging.categories.find((cat) => cat.code == category)
					const thread = {
						Id: result.ItemId,
						Subject: subject,
						CategoryName: cat ? cat.description : 'Unknown',
						IsArchived: false,
						IsAlert: false,
						CreatedOn: result.Date,
						UpdatedOn: result.Date,
						UnreadMessageCount: 0
					}

					dispatch(addThreadAndRemoveDraft(thread))

					return thread
				})
		)
	}
}

export function _setCategories(categories){
	return {
		type: THREAD_LOAD_CATEGORIES,
		categories: categories
	}
}

export function getCategories(){
	return (dispatch) =>
		loadingActions.promise(
			dispatch,
			THREAD_LOAD_CATEGORIES,
			messagingAPI.getCategories()
				.then((categories) => dispatch(_setCategories(categories)))
		)
}



// WEBPACK FOOTER //
// ./node_modules/mb-app-common/store/actions/messaging.js